import { EmissionsCategoryEnum } from '@shared/api/types';
import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/radio-group';
import { cn } from '@shared/lib/utils';
import { useState } from 'react';

import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { useIntercom } from '@app/services/intercom/IntercomContext';
import { Icons } from '@shared/components/content/icons';
import { useToast } from '@shared/components/ui/use-toast';

import cloudA from '@shared/components/ratings/clouds/cloud-a.svg';
import cloudB from '@shared/components/ratings/clouds/cloud-b.svg';
import cloudC from '@shared/components/ratings/clouds/cloud-c.svg';
import cloudD from '@shared/components/ratings/clouds/cloud-d.svg';
import cloudE from '@shared/components/ratings/clouds/cloud-e.svg';
import LabelVariant from '@shared/components/ratings/label-variant';

type Variant = 'full' | 'cloud-scale' | 'cloud';
type Format = 'png' | 'svg';
type RatingSelection = EmissionsCategoryEnum | 'all';

const RATINGS: EmissionsCategoryEnum[] = ['A', 'B', 'C', 'D', 'E'];

const cloudSvgs: Record<EmissionsCategoryEnum, string> = {
  A: cloudA,
  B: cloudB,
  C: cloudC,
  D: cloudD,
  E: cloudE,
};

const VARIANTS: {
  value: Variant;
  label: string;
  hint: string;
  imgClass: string;
}[] = [
  {
    value: 'cloud',
    label: 'Cloud',
    hint: 'Our simplest label, useful for menus where space is limited',
    imgClass: 'h-16',
  },
  {
    value: 'cloud-scale',
    label: 'Cloud + scale',
    hint: 'A more informative label, ideal for marketing materials',
    imgClass: 'h-20',
  },
  {
    value: 'full',
    label: 'Full label',
    hint: 'Our complete label, required for use on product packaging',
    imgClass: 'h-24',
  },
];

// Ratings shown in variant previews when "all labels" is selected
const PREVIEW_RATING = 'A';

const FORMATS: {
  value: Format;
  label: string;
  hint: string;
  icon: keyof typeof Icons;
}[] = [
  {
    value: 'png',
    label: 'PNG',
    hint: 'Standard image format for use in websites and simple documents',
    icon: 'fileImage',
  },
  {
    value: 'svg',
    label: 'SVG',
    hint: 'Scalable vector image format for use with design software',
    icon: 'fileCode',
  },
];

interface LabelDownloadModalProps {
  open: boolean;
  onClose: () => void;
  defaultRating?: EmissionsCategoryEnum;
}

function ChoiceCard({
  children,
  selected,
  className,
}: {
  children: React.ReactNode;
  selected: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'cursor-pointer rounded-lg border-2 border-input transition-colors',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
        selected && 'border-primary-500 bg-primary-50/20',
        className
      )}
    >
      {children}
    </div>
  );
}

export function LabelDownloadModal({
  open,
  onClose,
  defaultRating,
}: LabelDownloadModalProps) {
  const [selectedRating, setSelectedRating] = useState<RatingSelection>(
    defaultRating ?? 'all'
  );
  const [selectedVariant, setSelectedVariant] = useState<Variant>('cloud');
  const [selectedFormat, setSelectedFormat] = useState<Format>('png');
  const { track } = useAnalytics();
  const { showIntercomArticle } = useIntercom();
  const { toast } = useToast();

  const s3Base = import.meta.env.VITE_S3_ASSETS_URL as string;
  const ratingKey =
    selectedRating === 'all' ? null : selectedRating.toLowerCase();

  const previewRating =
    selectedRating === 'all' ? PREVIEW_RATING : selectedRating;

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    const path = ratingKey
      ? `labels/${selectedVariant}/${ratingKey}.${selectedFormat}`
      : `labels/${selectedVariant}/all-labels-${selectedFormat}.zip`;
    const filename = ratingKey
      ? `my-emissions-label-${selectedVariant}-${ratingKey}.${selectedFormat}`
      : `my-emissions-labels-${selectedVariant}-all-${selectedFormat}.zip`;

    setIsDownloading(true);
    try {
      const res = await fetch(`${s3Base}${path}`);
      if (!res.ok) throw new Error(`${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      track('Label Downloaded', {
        rating: selectedRating,
        variant: selectedVariant,
        format: selectedFormat,
      });
      toast({
        title: 'Download complete',
        description: 'Your label has been downloaded.',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Download failed',
        description:
          'Please try again or get in touch if the problem persists.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
    // Do NOT close modal so user can download more variants
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-2xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Download carbon labels</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          {/* Select a label */}
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Select a label</p>
            <RadioGroup
              value={selectedRating}
              onValueChange={(v) => setSelectedRating(v as RatingSelection)}
              className="flex gap-2"
            >
              {RATINGS.map((r) => (
                <label
                  key={r}
                  htmlFor={`rating-${r}`}
                  className="flex-1 cursor-pointer"
                >
                  <ChoiceCard
                    selected={selectedRating === r}
                    className="flex flex-col items-center p-3"
                  >
                    <img
                      src={cloudSvgs[r]}
                      className="size-10"
                      alt={`Rating ${r}`}
                    />
                    <RadioGroupItem
                      value={r}
                      id={`rating-${r}`}
                      className="sr-only"
                    />
                  </ChoiceCard>
                </label>
              ))}
              <label htmlFor="rating-all" className="flex-1 cursor-pointer">
                <ChoiceCard
                  selected={selectedRating === 'all'}
                  className="flex h-full items-center justify-center px-4 py-3"
                >
                  <span className="text-sm font-medium">All labels</span>
                  <RadioGroupItem
                    value="all"
                    id="rating-all"
                    className="sr-only"
                  />
                </ChoiceCard>
              </label>
            </RadioGroup>
          </div>

          {/* Select a style */}
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Select a style</p>
            <RadioGroup
              value={selectedVariant}
              onValueChange={(v) => setSelectedVariant(v as Variant)}
              className="flex gap-2"
            >
              {VARIANTS.map(({ value, label, hint, imgClass }) => (
                <label
                  key={value}
                  htmlFor={`variant-${value}`}
                  className="flex-1 cursor-pointer"
                >
                  <ChoiceCard
                    selected={selectedVariant === value}
                    className="flex h-52 flex-col items-center gap-2 p-3"
                  >
                    <div className="flex flex-1 items-center justify-center">
                      <LabelVariant
                        variant={value}
                        rating={previewRating}
                        className={imgClass}
                      />
                    </div>
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-center text-sm font-medium">
                        {label}
                      </span>
                      <span className="text-center text-xs text-muted-foreground">
                        {hint}
                      </span>
                    </div>
                    <RadioGroupItem
                      value={value}
                      id={`variant-${value}`}
                      className="sr-only"
                    />
                  </ChoiceCard>
                </label>
              ))}
            </RadioGroup>
          </div>

          {/* Select a format */}
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">Select a format</p>
            <RadioGroup
              value={selectedFormat}
              onValueChange={(v) => setSelectedFormat(v as Format)}
              className="flex gap-2"
            >
              {FORMATS.map(({ value: f, label, hint, icon }) => (
                <label
                  key={f}
                  htmlFor={`format-${f}`}
                  className="flex-1 cursor-pointer"
                >
                  <ChoiceCard
                    selected={selectedFormat === f}
                    className="flex flex-col gap-0.5 px-4 py-3"
                  >
                    <div className="flex items-center gap-1.5">
                      {(() => {
                        const Icon = Icons[icon];
                        return (
                          <Icon className="size-4 text-muted-foreground" />
                        );
                      })()}
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {hint}
                    </span>
                    <RadioGroupItem
                      value={f}
                      id={`format-${f}`}
                      className="sr-only"
                    />
                  </ChoiceCard>
                </label>
              ))}
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="mt-2 flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Before publishing materials or printing packaging with our labels,
            please read our{' '}
            <button
              type="button"
              className="text-primary underline underline-offset-2"
              onClick={() => showIntercomArticle('carbonLabelling')}
            >
              labelling guidelines
            </button>
            .
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleDownload} loading={isDownloading}>
              Download
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
